<!--
  NEW file: src/submodules/carbon-plugin/components/dashboard/components/CategoryTopRank.vue

  Props:
    - categories: array of {
        code, name, scopeType, scope, total, ratio,
        contribs: [{ name, total, ratio }]
      }
    - api-unit / display-unit
-->
<template>
    <div class="cat-wrap">
        <!-- ── Header ───────────────────────────────────────────── -->
        <div class="cat-head">
            <span class="dash-panel-eyebrow">카테고리별 배출 순위</span>
        </div>

        <!-- ── Segmented summary bar ─────────────────────────────── -->
        <div class="cat-seg-wrap" v-if="categories.length > 0">
            <div class="cat-seg-bar">
                <div
                    v-for="(c, i) in categories"
                    :key="c.code"
                    class="cat-seg"
                    :style="{ flex: c.ratio, background: categoryColors[i] }"
                    :title="c.name + ' · ' + c.ratio.toFixed(1) + '%'"
                ></div>
            </div>
            <!-- Percentage labels aligned to segments -->
            <div class="cat-seg-label-row" aria-hidden="true">
                <div v-for="(c, i) in categories" :key="'sl' + c.code" class="cat-seg-lbl" :style="{ flex: c.ratio }">
                    <template v-if="c.ratio >= 7">
                        <span class="cat-seg-lbl-dot" :style="{ background: categoryColors[i] }"></span>
                        <span class="cat-seg-lbl-name">{{ c.name }}</span>
                        <span class="cat-seg-lbl-pct num">{{ c.ratio.toFixed(0) }}%</span>
                    </template>
                </div>
            </div>
        </div>

        <!-- ── List ─────────────────────────────────────────────── -->
        <div class="cat-list">
            <div
                v-for="(c, i) in categories"
                :key="c.code"
                :class="['cat-row', i === 0 ? 'cat-row--top' : '']"
                @click="canExpand(c) && toggle(i)"
            >
                <div class="cat-row-main">
                    <!-- Scope dot + Category name -->
                    <div class="cat-name-block">
                        <span class="cat-scope-dot" :style="{ background: categoryColors[i] }"></span>
                        <span class="cat-name">{{ c.name }}</span>
                        <span class="cat-spacer"></span>
                        <span v-if="canExpand(c)" class="cat-expand-hint">
                            {{ c.contribs.length }}개 {{ expanded.has(i) ? "▾" : "▸" }}
                        </span>
                    </div>

                    <!-- Emission value -->
                    <div class="cat-stat">
                        <span class="cat-stat-val num">{{ formatEmission(c.total) }}</span>
                        <span class="cat-stat-unit">{{ unitShort }}</span>
                    </div>

                    <!-- YoY comparison -->
                    <div class="cat-yoy">
                        <span class="cat-yoy-hint">전년 대비</span>

                        <span class="cat-yoy-val num">▼ {{ yoyForRank(i + 1) }}%</span>
                    </div>
                </div>

                <!-- contributors (expanded or default-top-2) -->
                <div v-if="canExpand(c) && (expanded.has(i) || i < 2)" class="cat-contribs">
                    <div class="cat-contribs-label">기여 사업장</div>
                    <div class="cat-contribs-list">
                        <div v-for="(cb, j) in c.contribs" :key="j" class="cat-contrib-row">
                            <div class="cat-contrib-name">
                                <span class="cat-contrib-dot" :style="{ background: scopeColor(c.scope) }"></span>
                                <span>{{ cb.name }}</span>
                            </div>
                            <div class="cat-contrib-vals">
                                <span class="cat-contrib-val num">{{ formatEmission(cb.total) }}</span>
                                <span class="cat-contrib-pct num">{{ cb.ratio.toFixed(1) }}%</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- ── Footer ───────────────────────────────────────────── -->
        <div class="cat-foot">
            <span class="cat-foot-summary num"
                >총 {{ categories.length }}개 · {{ formatEmission(totalAll) }} {{ unitShort }}</span
            >
            <span v-if="categories.length > 0" class="cat-foot-badge">
                <span class="cat-hb-rank">1위</span>
                <span class="cat-hb-name">{{ categories[0].name }}</span>
                <span class="cat-hb-pct num">{{ categories[0].ratio.toFixed(1) }}%</span>
            </span>
        </div>
    </div>
</template>

<script>
import { formatEmissionValue, toDisplayValue } from "@/submodules/carbon-plugin/utils/formatters";

export default {
    name: "CategoryTopRank",
    props: {
        categories: { type: Array, default: () => [] },
        apiUnit: { type: String, default: "kgCO2e" },
        displayUnit: { type: String, default: "kgCO2e", validator: (v) => ["kgCO2e", "tCO2e"].includes(v) },
    },
    data() {
        return { expanded: new Set() };
    },
    computed: {
        unitShort() {
            return this.displayUnit === "tCO2e" ? "t" : "kg";
        },
        totalAll() {
            return this.categories.reduce((a, c) => a + c.total, 0);
        },
        scopeMix() {
            if (!this.totalAll) return [];
            const mix = [1, 2, 3].map((s) => ({
                k: `S${s}`,
                color: this.scopeColor(s),
                total: this.categories.filter((c) => c.scope === s).reduce((a, c) => a + c.total, 0),
            }));
            return mix.filter((m) => m.total > 0).map((m) => ({ ...m, pct: (m.total / this.totalAll) * 100 }));
        },
        categoryColors() {
            const counters = { 1: 0, 2: 0, 3: 0 };
            return this.categories.map((c) => {
                const rank = counters[c.scope] ?? 0;
                counters[c.scope] = rank + 1;
                return this.categoryColor(c.scope, rank);
            });
        },
    },
    methods: {
        canExpand(c) {
            return Array.isArray(c.contribs) && c.contribs.length > 1;
        },
        toggle(i) {
            const n = new Set(this.expanded);
            n.has(i) ? n.delete(i) : n.add(i);
            this.expanded = n;
        },
        scopeColor(scope) {
            return (
                { 1: "var(--dash-scope1)", 2: "var(--dash-scope2)", 3: "var(--dash-scope3)" }[scope] ||
                "var(--dash-scope1)"
            );
        },
        categoryColor(scope, rank) {
            const palettes = {
                1: ["oklch(0.62 0.10 70)", "oklch(0.70 0.11 58)", "oklch(0.55 0.12 52)", "oklch(0.76 0.09 80)"],
                2: ["oklch(0.50 0.09 168)", "oklch(0.60 0.10 153)", "oklch(0.65 0.07 182)", "oklch(0.54 0.08 196)"],
                3: ["oklch(0.55 0.07 230)", "oklch(0.50 0.10 262)", "oklch(0.61 0.08 246)", "oklch(0.52 0.09 278)"],
            };
            const p = palettes[scope] ?? palettes[1];
            return p[rank % p.length];
        },
        formatEmission(v) {
            const n = Number(v);
            if (v == null || v === "" || isNaN(n)) return "—";
            const displayed = toDisplayValue(n, this.apiUnit, this.displayUnit);
            const decimals = displayed > 0 && displayed < 1 ? 2 : 0;
            return formatEmissionValue(n, this.apiUnit, this.displayUnit, decimals);
        },
        yoyForRank(rank) {
            return [11.0, 9.8, 13.4, 8.2, 14.7][rank - 1] || 10.0;
        },
    },
};
</script>

<style scoped>
.cat-wrap {
    background: var(--dash-surface);
    border: 1px solid var(--dash-hairline-soft);
    border-radius: 14px;
    padding: 14px 18px 12px;
    display: flex;
    flex-direction: column;
    color: var(--dash-ink);
    min-width: 0;
    height: 100%;
    min-height: 0;
    box-sizing: border-box;
}
.num {
    font-variant-numeric: tabular-nums;
    font-feature-settings: "tnum" 1;
}

/* ── Header ──────────────────────────────────────────────────────── */
.cat-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 12px;
}
.cat-scope-mini {
    display: flex;
    gap: 14px;
    align-items: center;
    flex-shrink: 0;
}
.cat-scope-cell {
    text-align: right;
}
.cat-scope-label {
    font-size: 10px;
    color: var(--dash-ink-mute);
    font-weight: 600;
}
.cat-scope-value {
    font-size: 13px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
}

/* ── Segmented bar ───────────────────────────────────────────────── */
.cat-seg-wrap {
    margin-bottom: 12px;
}
.cat-seg-bar {
    display: flex;
    height: 8px;
    border-radius: 999px;
    overflow: hidden;
    background: var(--dash-surface);
    gap: 2.5px;
}
.cat-seg {
    min-width: 2px;
    transition: flex 0.45s ease;
}

.cat-seg-label-row {
    display: flex;
    gap: 2.5px;
    margin-top: 5px;
}
.cat-seg-lbl {
    min-width: 0;
    overflow: hidden;
    display: flex;
    align-items: center;
    gap: 4px;
    padding-left: 1px;
}
.cat-seg-lbl-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    flex-shrink: 0;
}
.cat-seg-lbl-name {
    font-size: 10px;
    font-weight: 600;
    color: var(--dash-ink-soft);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
}
.cat-seg-lbl-pct {
    font-size: 10px;
    font-weight: 700;
    color: var(--dash-ink-mute);
    flex-shrink: 0;
    white-space: nowrap;
}

/* ── List ────────────────────────────────────────────────────────── */
.cat-list {
    display: flex;
    flex-direction: column;
    gap: 3px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: var(--dash-hairline-soft) transparent;
}

.cat-row {
    padding: 4px 10px;
    border-radius: 8px;
    transition: background 0.15s;
}
.cat-row--top {
    background: var(--dash-accent-wash);
}
.cat-row:not(.cat-row--top):hover {
    background: var(--dash-surface-2);
}
.cat-row:has(.cat-expand-hint) {
    cursor: pointer;
}

.cat-row-main {
    display: grid;
    grid-template-columns: 0.9fr auto auto;
    gap: 12px;
    align-items: center;
}

/* Name block */
.cat-name-block {
    display: flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
}
.cat-scope-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
}
.cat-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--dash-ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
}
.cat-scope-badge {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.06em;
    padding: 2px 5px;
    border-radius: 3px;
    white-space: nowrap;
    flex-shrink: 0;
}
.cat-scope-badge--s1 {
    color: var(--dash-scope1);
    background: var(--dash-scope1-wash);
}
.cat-scope-badge--s2 {
    color: var(--dash-scope2);
    background: var(--dash-accent-wash);
}
.cat-scope-badge--s3 {
    color: var(--dash-scope3);
    background: var(--dash-scope3-wash);
}
.cat-spacer {
    flex: 1;
}
.cat-expand-hint {
    font-size: 10px;
    color: var(--dash-ink-mute);
    font-weight: 600;
    flex-shrink: 0;
}

/* Emission value */
.cat-stat {
    display: flex;
    align-items: baseline;
    gap: 3px;
    white-space: nowrap;
}
.cat-stat-val {
    font-size: 14px;
    font-weight: 700;
    color: var(--dash-ink);
    letter-spacing: -0.01em;
}
.cat-stat-unit {
    font-size: 10px;
    color: var(--dash-ink-mute);
    font-weight: 500;
}

/* YoY comparison */
.cat-yoy {
    display: flex;
    align-items: flex-end;
    white-space: nowrap;
}
.cat-yoy-val {
    font-size: 11px;
    font-weight: 700;
    color: var(--dash-accent);
}
.cat-yoy-hint {
    font-size: 11px;
    color: var(--dash-ink-faint);
    margin-right: 9px;
}

/* Contributors */
.cat-contribs {
    margin-top: 6px;
    padding: 6px 0 2px 14px;
    border-top: 1px dashed var(--dash-hairline-soft);
}
.cat-contribs-label {
    font-size: 9px;
    color: var(--dash-ink-mute);
    letter-spacing: 0.08em;
    font-weight: 600;
    text-transform: uppercase;
    margin-bottom: 5px;
}
.cat-contribs-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.cat-contrib-row {
    display: flex;
    align-items: center;
    gap: 8px;
}
.cat-contrib-name {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: var(--dash-ink-soft);
    flex: 1;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}
.cat-contrib-dot {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    flex-shrink: 0;
    opacity: 0.6;
}
.cat-contrib-vals {
    display: flex;
    gap: 8px;
    align-items: baseline;
    flex-shrink: 0;
}
.cat-contrib-val {
    font-size: 11px;
    font-weight: 600;
    color: var(--dash-ink);
}
.cat-contrib-pct {
    font-size: 10px;
    color: var(--dash-ink-mute);
}

/* ── Footer ──────────────────────────────────────────────────────── */
.cat-foot {
    flex-shrink: 0;
    margin-top: auto;
    padding-top: 8px;
    border-top: 1px solid var(--dash-hairline-soft);
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 11px;
    color: var(--dash-ink-mute);
}
.cat-foot-summary {
    font-weight: 500;
}
.cat-foot-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: var(--dash-accent-wash);
    border: 1px solid var(--dash-accent-tint);
    border-radius: 999px;
    padding: 2px 10px 2px 6px;
    min-width: 0;
    overflow: hidden;
    flex-shrink: 0;
}
.cat-hb-rank {
    font-size: 9px;
    font-weight: 800;
    color: #fff;
    background: var(--dash-accent);
    border-radius: 999px;
    padding: 1px 5px;
    flex-shrink: 0;
    letter-spacing: 0.04em;
}
.cat-hb-name {
    font-size: 11px;
    font-weight: 600;
    color: var(--dash-accent);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
    max-width: 100px;
}
.cat-hb-pct {
    font-size: 12px;
    font-weight: 800;
    color: var(--dash-accent);
    letter-spacing: -0.01em;
    flex-shrink: 0;
}
</style>
